import CustomForm from "@components/form/CustomForm";
import PageWrapper from "@components/layouts/PageWrapper";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useErrorHandling } from "@hooks/useErrorHandling";
import { castTypeArr } from "@utils/general/cast-type";
import { TFailResponse } from "@type-defs/general/TFailResponse";
import { removeKeys } from "@utils/general/removeKeys";
import { TSuccessResponse } from "@type-defs/general/TSuccessResponse";
import { Toast } from "@helpers/popups/Toast";
import { ALLOCATION_ACCOUNT } from "@utils/constants/allocation-account.constants";

import {
  useGetAllMainWarehouseListQuery,
  useGetAllStatesQuery,
} from "@states/common/common.api";
import {
  addCities,
  addStates,
  addTownships,
  emptyCity,
  emptyTownship,
  resetFormValues,
} from "@states/common/common.slice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@states/store";
import { LoadingSpinner } from "@components/layouts/LoadingSpinner";
import { allocationAccountFields } from "@helpers/fields/allocation-and-inventory/allocation-account.fields";
import { useCreateWarehouseMutation } from "@states/warehouse-and-inventory/warehouse.api";
import { WAREHOUSE } from "@utils/constants/warehouse.constants";
import { allocationAccountSchema } from "@helpers/validation-schemas/warehouse-and-inventory/allocation-account.schema";
import BackButton from "@components/buttons/BackButton";
import { Controller, useForm } from "react-hook-form";
import CustomRadioGroup from "@components/form/CustomRadioGroup";
import AuthComponent from "@components/auth/component";

const AllocationAccountAddPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [cities, setCities] = useState();
  const [townships, setTownships] = useState();

  const [selectedType, setSelectedType] = useState(2);
  const { formValues } = useSelector((state: RootState) => state.common);

  const [createWarehoue, { isLoading, isSuccess, error: createError }] =
    useCreateWarehouseMutation();
  const { data: warehouseData } = useGetAllMainWarehouseListQuery();

  const { data: stateData } = useGetAllStatesQuery();

  const {
    states: storedStates,
    cities: storedCities,
    townships: storedTownships,
  } = useSelector((state: RootState) => state.common);

  useErrorHandling(...castTypeArr<TFailResponse>([createError]));
  const fields = useMemo(() => {
    return allocationAccountFields({
      data: {
        states: stateData || [],
        cities: cities || [],
        townships: townships || [],
        warehouseData: warehouseData || [],
      },
      selectActions: {
        stateAction: addStates,
        cityAction: addCities,
        townshipAction: addTownships,
      },
      disabled: {
        cityDisabled: storedStates.length <= 0,
        townshipDisabled: storedCities.length <= 0,
      },
      selectSingleIds: {
        stateId: storedStates[0]?.warehouse_division_id,
        cityId: storedCities[0]?.warehouse_city_id,
        townshipId: storedTownships[0]?.warehouse_township_id,
      },
      values: {
        country: 1,
        selectedType,
        // warehouseValue: null,
      },
    });
  }, [
    formValues,
    stateData,
    cities,
    warehouseData,
    townships,
    storedStates,
    storedCities,
    storedTownships,
    selectedType,
  ]);
  useEffect(() => {
    if (storedStates.length > 0) {
      const state = storedStates[0];

      if (stateData) {
        const foundState: any = stateData.find(
          (sd: any) => sd.stateid == state.warehouse_division_id
        );

        if (foundState) {
          setCities(
            foundState.cities.map((c: any) => ({
              ...c,
              state_id: foundState.stateid,
              state_name: foundState.name,
            }))
          );
        }
      }
    }
  }, [storedStates, stateData]);

  useEffect(() => {
    if (storedCities.length > 0) {
      const state = storedStates[0];
      const city = storedCities[0];

      if (stateData) {
        const foundState: any = stateData.find(
          (sd: any) => sd.stateid == state.warehouse_division_id
        );

        if (foundState) {
          const foundCity = foundState.cities.find(
            (c: any) => c.cityid == city.warehouse_city_id
          );

          console.log(foundCity, "foundCity");

          const formatted = {
            ...foundCity,
            state_id: foundState.stateid,
            state_name: foundState.name,
          };

          if (formatted) {
            setTownships(
              formatted.townships.map((t: any) => ({
                ...t,
                state_id: foundState.stateid,
                state_name: foundState.name,
                city_id: foundCity.cityid,
                city_name: foundCity.cityname,
              }))
            );
          }
        }
      }
    }
  }, [storedCities, stateData]);
  useEffect(() => {
    if (formValues?.warehouse_type) {
      setSelectedType(formValues?.warehouse_type);
    }
  }, [formValues?.warehouse_type]);
  console.log("formValues", formValues);
  const asyncDispatcher = useCallback(
    async (reqBody: any) => {
      try {
        const rest = removeKeys(
          reqBody,
          "warehouse_division_name",
          "warehouse_city_name",
          "warehouse_township_name"
        );

        console.log("rest", rest);

        const res = await createWarehoue({
          warehouse_type: selectedType,
          ...rest,
        });

        const successData = "data" in res ? res.data : null;
        const { success, message } = successData as TSuccessResponse;

        if (success || isSuccess) {
          await Toast.fire({
            title: message,
            icon: "success",
          });

          navigate(ALLOCATION_ACCOUNT.BASE_PATH, {
            state: {
              reload: true,
            },
          });
        }
      } catch (error) {
        console.log("Error", error);
      }
    },
    [createWarehoue, isSuccess, Toast, navigate, selectedType]
  );

  return (
    <AuthComponent
      action="create"
      module="Stock & Inventory"
      subModule="Allocation Account"
      page
    >
      <PageWrapper>
        {isLoading && <LoadingSpinner />}
        <BackButton />
        <h3 className="mb-4 ml-2 text-2xl font-[600] text-primary-dark uppercase">
          {ALLOCATION_ACCOUNT.ADD_BUTTON_TITLE}
        </h3>

        <CustomForm
          schema={allocationAccountSchema}
          backbtn={false}
          fields={fields}
          asyncDispatcher={asyncDispatcher}
        />
      </PageWrapper>
    </AuthComponent>
  );
};

export default AllocationAccountAddPage;
